import type { IRideDto } from "../../dto/rides/ride.dto";
import type { IRideBookingInfoDto } from "../../models/ride-booking.models";
import { normalizePositiveIntId } from "../numbers.helpers";

export type GetRideBookingsRouteMismatchSummaryParams = {
  rideDto: Partial<IRideDto> | undefined;
  rideBookings: IRideBookingInfoDto[] | undefined;
};

/** Gaps between route metadata (`passengerIds` / `passengersInfo`) and bookings on this ride. */
export type RideBookingsRouteMismatchSummary = {
  /** Booking IDs the route expects (from `passengersInfo` for route passengers) but this ride has no such booking. */
  routeBookingIdsMissingFromRide: number[];
  /** Passenger id(s) listed on the route with no ride booking and no booking id in metadata (or inconsistent). */
  routePassengerIdsWithoutRideBooking: number[];
  /** Booking id(s) on this ride whose passenger is not listed in route `passengerIds`. */
  bookingIdsOnRideNotOnRoutePassengers: number[];
};

const uniqueSorted = (ids: number[]): number[] =>
  [...new Set(ids)].sort((a, b) => a - b);

/**
 * Compares route `passengerIds` / `passengersInfo` with the current ride booking list.
 * Used for the bookings mismatch warning tooltip (comma-separated id lists).
 */
export const getRideBookingsRouteMismatchSummary = ({
  rideDto,
  rideBookings,
}: GetRideBookingsRouteMismatchSummaryParams): RideBookingsRouteMismatchSummary => {
  const bookings = rideBookings ?? [];
  const rideBookingIdSet = new Set(bookings.map(b => b.id));
  const ridePassengerIds = new Set(
    bookings
      .map(b => normalizePositiveIntId(b.passengerId))
      .filter((id): id is number => id !== undefined),
  );

  const routePassengerIds = uniqueSorted(
    (rideDto?.metadata?.passengerIds ?? [])
      .map(normalizePositiveIntId)
      .filter((id): id is number => id !== undefined),
  );
  const routePaxSet = new Set(routePassengerIds);

  const passengersInfo = rideDto?.metadata?.passengersInfo ?? [];

  const bookingIdsOnRideNotOnRoutePassengers: number[] = [];
  for (const b of bookings) {
    const pid = normalizePositiveIntId(b.passengerId);
    if (pid === undefined || !routePaxSet.has(pid)) {
      bookingIdsOnRideNotOnRoutePassengers.push(b.id);
    }
  }

  const routeBookingIdsMissingFromRide: number[] = [];
  const routePassengerIdsWithoutRideBooking: number[] = [];

  for (const pid of routePassengerIds) {
    if (ridePassengerIds.has(pid)) {
      continue;
    }
    const pInfo = passengersInfo.find(
      x => normalizePositiveIntId(x.id) === pid,
    );
    const metaBid = normalizePositiveIntId(pInfo?.bookingId);
    if (metaBid !== undefined && !rideBookingIdSet.has(metaBid)) {
      routeBookingIdsMissingFromRide.push(metaBid);
    } else {
      routePassengerIdsWithoutRideBooking.push(pid);
    }
  }

  return {
    routeBookingIdsMissingFromRide: uniqueSorted(
      routeBookingIdsMissingFromRide,
    ),
    routePassengerIdsWithoutRideBooking: uniqueSorted(
      routePassengerIdsWithoutRideBooking,
    ),
    bookingIdsOnRideNotOnRoutePassengers: uniqueSorted(
      bookingIdsOnRideNotOnRoutePassengers,
    ),
  };
};
